import { useEffect, useState, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import {
  History,
  TrendingUp,
  TrendingDown,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Search,
  Sparkles,
  Activity,
} from 'lucide-react';
import { Card } from '../components/common/Card';
import { SignalBadge } from '../components/common/Badge';
import { StockLogo } from '../components/common/StockLogo';
import { LockedFeature } from '../components/common/LockedFeature';
import { useAuthStore } from '../store';
import { getSimulatorSignals, getStockHistory, type HistoryDataPoint } from '../utils/api';
import { formatDate, formatCurrency } from '../utils/format';
import type { SimulatorSignal } from '../types';

type SimulatorMode = 'stock' | 'market';

// Combined Price + Sentiment chart component
function PriceSentimentChart({
  data,
  currentIndex,
  height = 200,
}: {
  data: HistoryDataPoint[];
  currentIndex: number;
  height?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const chartHeight = rect.height;
    const padding = { top: 20, right: 50, bottom: 30, left: 50 };

    ctx.clearRect(0, 0, width, chartHeight);

    // Get price range
    const prices = data.map((d) => d.price).filter((p): p is number => p !== undefined);
    if (prices.length === 0) return;

    const minPrice = Math.min(...prices) * 0.995;
    const maxPrice = Math.max(...prices) * 1.005;
    const priceRange = maxPrice - minPrice;

    // Get sentiment range (fixed -100 to 100)
    const sentimentMin = -100;
    const sentimentMax = 100;
    const sentimentRange = sentimentMax - sentimentMin;

    const chartWidth = width - padding.left - padding.right;
    const chartAreaHeight = chartHeight - padding.top - padding.bottom;

    // Calculate price points
    const pricePoints = data.map((d, i) => ({
      x: padding.left + (i / Math.max(1, data.length - 1)) * chartWidth,
      y: d.price !== undefined
        ? padding.top + (1 - (d.price - minPrice) / priceRange) * chartAreaHeight
        : null,
    }));

    // Calculate sentiment points
    const sentimentPoints = data.map((d, i) => ({
      x: padding.left + (i / Math.max(1, data.length - 1)) * chartWidth,
      y: d.sentiment_score !== undefined
        ? padding.top + (1 - (d.sentiment_score - sentimentMin) / sentimentRange) * chartAreaHeight
        : null,
    }));

    // Draw zero line for sentiment (center)
    const zeroY = padding.top + chartAreaHeight / 2;
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(100, 116, 139, 0.2)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.moveTo(padding.left, zeroY);
    ctx.lineTo(width - padding.right, zeroY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw sentiment area (filled)
    const hasSentiment = data.some(d => d.sentiment_score !== undefined);
    if (hasSentiment) {
      // Draw sentiment fill up to current index
      for (let i = 0; i <= currentIndex && i < sentimentPoints.length; i++) {
        const point = sentimentPoints[i];
        const sentiment = data[i].sentiment_score;
        if (point.y !== null && sentiment !== undefined) {
          const barWidth = chartWidth / data.length;
          const isPositive = sentiment >= 0;

          ctx.fillStyle = isPositive
            ? 'rgba(16, 185, 129, 0.15)'
            : 'rgba(239, 68, 68, 0.15)';

          if (isPositive) {
            ctx.fillRect(point.x - barWidth/2, point.y, barWidth, zeroY - point.y);
          } else {
            ctx.fillRect(point.x - barWidth/2, zeroY, barWidth, point.y - zeroY);
          }
        }
      }

      // Draw sentiment line (past - bright)
      ctx.beginPath();
      ctx.lineWidth = 2;
      let started = false;
      for (let i = 0; i <= currentIndex && i < sentimentPoints.length; i++) {
        const point = sentimentPoints[i];
        const sentiment = data[i].sentiment_score;
        if (point.y !== null) {
          ctx.strokeStyle = sentiment !== undefined && sentiment >= 0 ? '#10b981' : '#ef4444';
          if (!started) {
            ctx.moveTo(point.x, point.y);
            started = true;
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }
      }
      ctx.stroke();

      // Draw sentiment line (future - dimmed)
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.2)';
      ctx.lineWidth = 1;
      started = false;
      for (let i = currentIndex; i < sentimentPoints.length; i++) {
        const point = sentimentPoints[i];
        if (point.y !== null) {
          if (!started) {
            ctx.moveTo(point.x, point.y);
            started = true;
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }
      }
      ctx.stroke();
    }

    // Draw price gradient area up to current index
    const priceGradient = ctx.createLinearGradient(0, padding.top, 0, chartHeight - padding.bottom);
    priceGradient.addColorStop(0, 'rgba(59, 130, 246, 0.2)');
    priceGradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)');

    ctx.beginPath();
    let started = false;
    for (let i = 0; i <= currentIndex && i < pricePoints.length; i++) {
      const point = pricePoints[i];
      if (point.y !== null) {
        if (!started) {
          ctx.moveTo(point.x, point.y);
          started = true;
        } else {
          ctx.lineTo(point.x, point.y);
        }
      }
    }
    if (started && currentIndex < pricePoints.length) {
      const lastPoint = pricePoints[Math.min(currentIndex, pricePoints.length - 1)];
      if (lastPoint.y !== null) {
        ctx.lineTo(lastPoint.x, chartHeight - padding.bottom);
        ctx.lineTo(padding.left, chartHeight - padding.bottom);
        ctx.closePath();
        ctx.fillStyle = priceGradient;
        ctx.fill();
      }
    }

    // Draw price line (future - dimmed)
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
    ctx.lineWidth = 1;
    started = false;
    for (let i = currentIndex; i < pricePoints.length; i++) {
      const point = pricePoints[i];
      if (point.y !== null) {
        if (!started) {
          ctx.moveTo(point.x, point.y);
          started = true;
        } else {
          ctx.lineTo(point.x, point.y);
        }
      }
    }
    ctx.stroke();

    // Draw price line (past - bright)
    ctx.beginPath();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2.5;
    started = false;
    for (let i = 0; i <= currentIndex && i < pricePoints.length; i++) {
      const point = pricePoints[i];
      if (point.y !== null) {
        if (!started) {
          ctx.moveTo(point.x, point.y);
          started = true;
        } else {
          ctx.lineTo(point.x, point.y);
        }
      }
    }
    ctx.stroke();

    // Draw current position dots
    if (currentIndex < pricePoints.length) {
      // Price dot
      const pricePoint = pricePoints[currentIndex];
      if (pricePoint.y !== null) {
        ctx.beginPath();
        ctx.arc(pricePoint.x, pricePoint.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#3b82f6';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Sentiment dot
      const sentimentPoint = sentimentPoints[currentIndex];
      const sentiment = data[currentIndex].sentiment_score;
      if (sentimentPoint.y !== null && sentiment !== undefined) {
        ctx.beginPath();
        ctx.arc(sentimentPoint.x, sentimentPoint.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = sentiment >= 0 ? '#10b981' : '#ef4444';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    // Draw Y-axis labels
    ctx.font = '10px Inter, system-ui, sans-serif';

    // Price labels (left)
    ctx.fillStyle = '#3b82f6';
    ctx.textAlign = 'right';
    ctx.fillText(`$${maxPrice.toFixed(0)}`, padding.left - 8, padding.top + 4);
    ctx.fillText(`$${minPrice.toFixed(0)}`, padding.left - 8, chartHeight - padding.bottom);

    // Sentiment labels (right)
    ctx.textAlign = 'left';
    ctx.fillStyle = '#10b981';
    ctx.fillText('+100', width - padding.right + 8, padding.top + 4);
    ctx.fillStyle = '#6b7280';
    ctx.fillText('0', width - padding.right + 8, zeroY + 4);
    ctx.fillStyle = '#ef4444';
    ctx.fillText('-100', width - padding.right + 8, chartHeight - padding.bottom);

    // Legend
    ctx.textAlign = 'left';
    const legendY = chartHeight - 8;

    // Price legend
    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(padding.left + 10, legendY - 3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#6b7280';
    ctx.fillText('Price', padding.left + 20, legendY);

    // Sentiment legend
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(padding.left + 70, legendY - 3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#6b7280';
    ctx.fillText('Sentiment', padding.left + 80, legendY);

  }, [data, currentIndex, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height }}
      className="rounded-lg"
    />
  );
}

// Helper to get unique dates sorted
function getUniqueDates(signals: SimulatorSignal[]): string[] {
  const dates = [...new Set(signals.map((s) => s.date))];
  return dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
}

// Helper to group signals by date
function groupByDate(signals: SimulatorSignal[]): Map<string, SimulatorSignal[]> {
  const grouped = new Map<string, SimulatorSignal[]>();
  signals.forEach((signal) => {
    const existing = grouped.get(signal.date) || [];
    grouped.set(signal.date, [...existing, signal]);
  });
  return grouped;
}

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 2,
  'pro': 2,
  'enterprise': 3
};

export function Simulator() {
  // Check tier access
  const { user } = useAuthStore();
  const userTier = user?.subscription_tier || 'free';
  const hasAccess = tierLevel[userTier] >= 2; // Pro or higher

  // Market signals data
  const [signals, setSignals] = useState<SimulatorSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<SimulatorMode>('stock');

  // Stock playback state
  const [selectedTicker, setSelectedTicker] = useState<string>('AAPL');
  const [tickerSearch, setTickerSearch] = useState('');
  const [stockHistory, setStockHistory] = useState<HistoryDataPoint[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockDays, setStockDays] = useState(30);

  // Playback state (shared)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(500);

  // Fetch market signals
  const fetchSignals = async () => {
    try {
      setLoading(true);
      const data = await getSimulatorSignals();
      setSignals(data);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to fetch signals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stock history
  const fetchStockHistory = async (ticker: string) => {
    try {
      setStockLoading(true);
      const response = await getStockHistory(ticker, stockDays);
      const sorted = [...response.history].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setStockHistory(sorted);
      setCurrentIndex(0);
      setIsPlaying(false);
    } catch (error) {
      console.error('Failed to fetch stock history:', error);
      setStockHistory([]);
    } finally {
      setStockLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
  }, []);

  useEffect(() => {
    if (selectedTicker && mode === 'stock') {
      fetchStockHistory(selectedTicker);
    }
  }, [selectedTicker, stockDays, mode]);

  // Memoized market data
  const uniqueDates = useMemo(() => getUniqueDates(signals), [signals]);
  const signalsByDate = useMemo(() => groupByDate(signals), [signals]);

  // Current data based on mode
  const currentDate = mode === 'stock'
    ? stockHistory[currentIndex]?.date
    : uniqueDates[currentIndex];
  const maxIndex = mode === 'stock'
    ? stockHistory.length - 1
    : uniqueDates.length - 1;

  // Current stock data point
  const currentStockData = stockHistory[currentIndex];
  const previousStockData = currentIndex > 0 ? stockHistory[currentIndex - 1] : null;
  const priceChange = currentStockData?.price && previousStockData?.price
    ? ((currentStockData.price - previousStockData.price) / previousStockData.price) * 100
    : 0;

  // Market mode: Get signals for current date, organized by sentiment
  const currentDaySignals = signalsByDate.get(currentDate || '') || [];

  // Sort by disconnect score to find best opportunities
  const buyOpportunities = currentDaySignals
    .filter(s => s.disconnect_score > 50)
    .sort((a, b) => b.disconnect_score - a.disconnect_score)
    .slice(0, 5);

  const sellOpportunities = currentDaySignals
    .filter(s => s.disconnect_score < 50)
    .sort((a, b) => a.disconnect_score - b.disconnect_score)
    .slice(0, 5);

  // Calculate market mood for the day
  const avgSentiment = currentDaySignals.length > 0
    ? currentDaySignals.reduce((sum, s) => sum + (s.signal.includes('BUY') ? 1 : s.signal.includes('SELL') ? -1 : 0), 0) / currentDaySignals.length
    : 0;
  const marketMood = avgSentiment > 0.3 ? 'Bullish' : avgSentiment < -0.3 ? 'Bearish' : 'Mixed';

  // Playback effect
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= maxIndex) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, playbackSpeed);
    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed, maxIndex]);

  // Mode tabs
  const modes: Array<{ id: SimulatorMode; label: string; icon: React.ReactNode }> = [
    { id: 'stock', label: 'Single Stock', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'market', label: 'Market Overview', icon: <Activity className="h-4 w-4" /> },
  ];

  // Popular tickers for quick selection
  const popularTickers = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'TSLA', 'META', 'AMD'];

  // Playback controls component (reused in both modes)
  const PlaybackControls = ({ max }: { max: number }) => (
    <div className="space-y-4">
      {/* Timeline Slider */}
      <div className="space-y-2">
        <input
          type="range"
          min={0}
          max={Math.max(0, max)}
          value={currentIndex}
          onChange={(e) => setCurrentIndex(parseInt(e.target.value))}
          className="timeline-slider w-full h-3 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-[var(--color-text-muted)]">
          <span>Day 1</span>
          <span className="font-medium text-[var(--color-text-secondary)]">
            Day {currentIndex + 1} of {max + 1}
          </span>
          <span>Day {max + 1}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => { setCurrentIndex(0); setIsPlaying(false); }}
          className="p-2 rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] transition-colors"
        >
          <SkipBack className="h-4 w-4" />
        </button>
        <button
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          className="p-2 rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className={clsx(
            'p-4 rounded-xl font-medium transition-all',
            isPlaying
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          )}
        >
          {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
        </button>
        <button
          onClick={() => setCurrentIndex(Math.min(max, currentIndex + 1))}
          className="p-2 rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => { setCurrentIndex(max); setIsPlaying(false); }}
          className="p-2 rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] transition-colors"
        >
          <SkipForward className="h-4 w-4" />
        </button>

        <div className="ml-4 flex items-center gap-2">
          <span className="text-xs text-[var(--color-text-muted)]">Speed:</span>
          <select
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(parseInt(e.target.value))}
            className="h-8 px-2 rounded-lg text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          >
            <option value={1000}>0.5x</option>
            <option value={500}>1x</option>
            <option value={250}>2x</option>
            <option value={100}>4x</option>
          </select>
        </div>
      </div>
    </div>
  );

  // Stock card for market view
  const StockCard = ({ signal, type }: { signal: SimulatorSignal; type: 'buy' | 'sell' }) => (
    <div className={clsx(
      'p-4 rounded-xl border transition-all hover:scale-[1.02]',
      type === 'buy'
        ? 'bg-emerald-500/5 border-emerald-500/20'
        : 'bg-red-500/5 border-red-500/20'
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <StockLogo ticker={signal.ticker} size="md" />
          <div>
            <p className="font-bold text-[var(--color-text-primary)]">{signal.ticker}</p>
            <p className="text-xs text-[var(--color-text-muted)]">
              {formatCurrency(signal.entry_price)}
            </p>
          </div>
        </div>
        <SignalBadge signal={signal.signal} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-muted)]">Disconnect</span>
        <span className={clsx(
          'text-lg font-bold font-mono',
          type === 'buy' ? 'text-emerald-500' : 'text-red-500'
        )}>
          {signal.disconnect_score != null ? (
            <>{signal.disconnect_score.toFixed(0)}</>
          ) : '-'}
        </span>
      </div>
    </div>
  );

  const SimulatorContent = () => (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">Time Simulator</h1>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">
          Travel back in time to see how sentiment and prices evolved
        </p>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Mode Selector */}
        <div className="flex gap-2 p-1 bg-[var(--color-bg-secondary)] rounded-xl w-fit">
          {modes.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                setMode(m.id);
                setCurrentIndex(0);
                setIsPlaying(false);
              }}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                mode === m.id
                  ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              )}
            >
              {m.icon}
              {m.label}
            </button>
          ))}
        </div>

        {/* Single Stock Mode */}
        {mode === 'stock' && (
          <div className="space-y-6">
            {/* Stock Selector */}
            <Card>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-[var(--color-text-muted)]" />
                    <span className="text-sm font-medium text-[var(--color-text-secondary)]">Stock:</span>
                  </div>
                  <div className="relative flex-1 max-w-xs">
                    <input
                      type="text"
                      value={tickerSearch || selectedTicker}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setTickerSearch(val);
                        if (val.length >= 1 && val.length <= 5) {
                          setSelectedTicker(val);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tickerSearch) {
                          setSelectedTicker(tickerSearch);
                          setTickerSearch('');
                        }
                      }}
                      placeholder="Enter ticker..."
                      className={clsx(
                        'w-full h-9 px-3 rounded-lg text-sm font-mono',
                        'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
                        'text-[var(--color-text-primary)]',
                        'focus:outline-none focus:ring-2 focus:ring-blue-500/50'
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--color-text-muted)]">Days:</span>
                    <select
                      value={stockDays}
                      onChange={(e) => setStockDays(parseInt(e.target.value))}
                      className="h-9 px-3 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                </div>

                {/* Quick ticker buttons */}
                <div className="flex flex-wrap gap-2">
                  {popularTickers.map((ticker) => (
                    <button
                      key={ticker}
                      onClick={() => {
                        setSelectedTicker(ticker);
                        setTickerSearch('');
                      }}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                        selectedTicker === ticker
                          ? 'bg-blue-500 text-white'
                          : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)]'
                      )}
                    >
                      {ticker}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {stockLoading ? (
              null
            ) : stockHistory.length === 0 ? (
              <Card className="text-center py-16">
                <History className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
                  No Historical Data
                </h3>
                <p className="text-[var(--color-text-muted)] mt-2">
                  No historical data found for {selectedTicker}
                </p>
              </Card>
            ) : (
              <Card>
                <div className="space-y-6">
                  {/* Stock Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <StockLogo ticker={selectedTicker} size="lg" />
                      <div>
                        <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">
                          {selectedTicker}
                        </h2>
                        <p className="text-sm text-[var(--color-text-muted)]">
                          {currentDate ? formatDate(currentDate) : 'Select a date'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {currentStockData?.price && (
                        <>
                          <p className="text-3xl font-bold font-mono text-[var(--color-text-primary)]">
                            {formatCurrency(currentStockData.price)}
                          </p>
                          <p className={clsx(
                            'text-sm font-mono font-medium',
                            priceChange >= 0 ? 'text-emerald-500' : 'text-red-500'
                          )}>
                            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Combined Price + Sentiment Chart */}
                  <div className="bg-[var(--color-bg-secondary)] rounded-xl p-4">
                    <PriceSentimentChart data={stockHistory} currentIndex={currentIndex} height={220} />
                  </div>

                  {/* Data Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {currentStockData?.sentiment_score != null && (
                      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Sentiment</p>
                        <p className={clsx(
                          'text-xl font-bold font-mono',
                          currentStockData.sentiment_score >= 60 ? 'text-emerald-500' :
                          currentStockData.sentiment_score <= 40 ? 'text-red-500' : 'text-amber-500'
                        )}>
                          {currentStockData.sentiment_score.toFixed(0)}
                        </p>
                      </div>
                    )}
                    {currentStockData?.disconnect_score != null && (
                      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Disconnect</p>
                        <p className={clsx(
                          'text-xl font-bold font-mono',
                          currentStockData.disconnect_score >= 60 ? 'text-emerald-500' :
                          currentStockData.disconnect_score <= 40 ? 'text-red-500' : 'text-amber-500'
                        )}>
                          {currentStockData.disconnect_score.toFixed(0)}
                        </p>
                      </div>
                    )}
                    {currentStockData?.signal && (
                      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Signal</p>
                        <SignalBadge signal={currentStockData.signal} />
                      </div>
                    )}
                    {currentStockData?.posts_analyzed != null && (
                      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Posts Analyzed</p>
                        <p className="text-xl font-bold text-[var(--color-text-primary)]">
                          {currentStockData.posts_analyzed.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Playback Controls */}
                  <PlaybackControls max={maxIndex} />
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Market Overview Mode */}
        {mode === 'market' && (
          <div className="space-y-6">
            {loading ? (
              null
            ) : signals.length === 0 ? (
              <Card className="text-center py-16">
                <History className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
                  No Historical Data
                </h3>
                <p className="text-[var(--color-text-muted)] mt-2">
                  No market data available
                </p>
              </Card>
            ) : (
              <>
                {/* Date & Market Mood */}
                <Card>
                  <div className="text-center mb-6">
                    <p className="text-sm text-[var(--color-text-muted)]">Market snapshot for</p>
                    <p className="text-3xl font-bold text-[var(--color-text-primary)] mt-1">
                      {currentDate ? formatDate(currentDate) : 'Select a date'}
                    </p>
                    <div className={clsx(
                      'inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full',
                      marketMood === 'Bullish' ? 'bg-emerald-500/10 text-emerald-500' :
                      marketMood === 'Bearish' ? 'bg-red-500/10 text-red-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    )}>
                      {marketMood === 'Bullish' ? <TrendingUp className="h-4 w-4" /> :
                       marketMood === 'Bearish' ? <TrendingDown className="h-4 w-4" /> :
                       <Activity className="h-4 w-4" />}
                      <span className="font-medium">{marketMood} Market</span>
                    </div>
                  </div>

                  {/* Playback Controls */}
                  <PlaybackControls max={maxIndex} />
                </Card>

                {/* Opportunities Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Buy Opportunities */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--color-text-primary)]">
                          Buy Opportunities
                        </h3>
                        <p className="text-xs text-[var(--color-text-muted)]">
                          Highest positive disconnect
                        </p>
                      </div>
                    </div>

                    {buyOpportunities.length > 0 ? (
                      <div className="space-y-3">
                        {buyOpportunities.map((signal, i) => (
                          <StockCard key={`${signal.ticker}-${i}`} signal={signal} type="buy" />
                        ))}
                      </div>
                    ) : (
                      <Card className="text-center py-8">
                        <p className="text-sm text-[var(--color-text-muted)]">
                          No buy opportunities on this date
                        </p>
                      </Card>
                    )}
                  </div>

                  {/* Sell Signals */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--color-text-primary)]">
                          Sell Signals
                        </h3>
                        <p className="text-xs text-[var(--color-text-muted)]">
                          Highest negative disconnect
                        </p>
                      </div>
                    </div>

                    {sellOpportunities.length > 0 ? (
                      <div className="space-y-3">
                        {sellOpportunities.map((signal, i) => (
                          <StockCard key={`${signal.ticker}-${i}`} signal={signal} type="sell" />
                        ))}
                      </div>
                    ) : (
                      <Card className="text-center py-8">
                        <p className="text-sm text-[var(--color-text-muted)]">
                          No sell signals on this date
                        </p>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="text-center py-4">
                    <p className="text-3xl font-bold text-[var(--color-text-primary)]">
                      {currentDaySignals.length}
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)]">Total Signals</p>
                  </Card>
                  <Card className="text-center py-4">
                    <p className="text-3xl font-bold text-emerald-500">
                      {currentDaySignals.filter(s => s.signal.includes('BUY')).length}
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)]">Buy Signals</p>
                  </Card>
                  <Card className="text-center py-4">
                    <p className="text-3xl font-bold text-red-500">
                      {currentDaySignals.filter(s => s.signal.includes('SELL')).length}
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)]">Sell Signals</p>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // For users without access, wrap content in LockedFeature
  if (!hasAccess) {
    return (
      <LockedFeature feature="Travel back in time to replay historical sentiment signals and see how markets evolved" requiredTier="pro">
        <SimulatorContent />
      </LockedFeature>
    );
  }

  return <SimulatorContent />;
}
